import React, { useState, useEffect, useContext } from 'react';
import { Redirect, useLocation } from 'react-router-dom';
import qs from 'querystringify';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Link from '@material-ui/core/Link';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { captureException } from '@sentry/browser';

import { Dispatch } from 'src/config/Store';
import { getCurrentUser } from 'src/selectors/userSelectors';

import isEmailValid from '../helpers/emailValidator';
import { AppContext } from '../contexts/AppContext';
import auth from '../services/Auth';
import PageContainer from '../components/PageContainer';
import { colors } from '../themes/colors';

interface QueryParams {
  next?: unknown;
}

const styles = (theme) =>
  ({
    loginPage: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
    },
    loginMain: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexGrow: 1,
      backgroundColor: colors.gray7,
    },
    loginForm: {
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'white',
      paddingTop: 48,
      paddingLeft: 38,
      paddingRight: 38,
      paddingBottom: 32,
      height: 500,
      maxWidth: 400,
    },
    title: {
      color: colors.blue4,
      marginBottom: 45,
    },
    input: {
      marginBottom: 24,
      height: 70,
    },
    loginButtonHolder: {
      marginTop: 24,
      marginBottom: 24,
      display: 'flex',
      justifyContent: 'center',
    },
    loginButton: {
      height: 42,
      width: 200,
    },
    loginButtonText: {
      fontSize: 18,
    },
    loginFooter: {
      display: 'flex',
      justifyContent: 'space-between',
      minHeight: 190,
      paddingLeft: 40,
      paddingRight: 40,
      paddingTop: 30,
      paddingBottom: 30,
      [theme.breakpoints.down('xs')]: {
        borderTop: `solid ${theme.mhBorderWidth} ${theme.mhBorderColor}`,
        paddingLeft: 25,
        paddingRight: 25,
        paddingTop: 20,
        paddingBottom: 20,
      },
    },
    footerSection: {
      display: 'flex',
      flexDirection: 'column',
    },
    leftFooterSection: {
      justifyContent: 'space-around',
      alignItems: 'flex-start',
    },
    rightFooterSection: {
      justifyContent: 'center',
      alignItems: 'flex-end',
    },
    modernHealthFooterText: {
      fontSize: 20,
      color: colors.blue4,
      [theme.breakpoints.down('xs')]: {
        fontSize: 16,
      },
    },
    normalFooterText: {
      color: colors.gray8,
      fontSize: 16,
      [theme.breakpoints.down('xs')]: {
        fontSize: 12,
      },
    },
    hrefUnderLine: {
      color: colors.gray8,
    },
  } as const);

const useStyles = makeStyles(styles);

const mapState = (state) => ({
  currentUser: getCurrentUser(state),
});

const SsoLogin: React.FC = () => {
  const location = useLocation();
  const contextType = useContext(AppContext);
  const [fieldErrors, setFieldErrors] = useState({ email: '' });
  const [email, setEmail] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loading, setLoading] = useState(false);

  const classes = useStyles();
  const dispatch = useDispatch<Dispatch>();
  const { currentUser } = useSelector(mapState);

  const login = async (event) => {
    event.preventDefault();
    setLoginError('');
    try {
      if (isEmailValid(email)) {
        setLoading(true);
        const ssoUrl = await auth.ssoLoginRequest({ email: email });
        window.location.href = ssoUrl;
      } else {
        setFieldErrors({ email: 'Please enter a valid email' });
      }
    } catch (e: unknown) {
      if (e instanceof Error) {
        setLoginError(e.message);
      }
      setFieldErrors({ email: 'SSO not enabled for email address' });
      setLoading(false);
    }
  };

  const onLoginClick = (event) => {
    login(event);
  };

  useEffect(() => {
    const params = qs.parse(location.search);

    async function refreshSSOToken() {
      try {
        await auth.refreshSSOToken(params['token']);
        dispatch.user.setIsAuthenticated(true);
        contextType.initializeApp();
      } catch (e: unknown) {
        setLoading(false);
        captureException(e);
        if (typeof e === 'string') {
          setLoginError(e);
        } else if (e instanceof Error) {
          setLoginError(e.message);
        }
      }
    }
    if (params['token']) {
      setLoading(true);
      refreshSSOToken();
    }
  }, [location, dispatch]);

  const params = qs.parse(location.search) as QueryParams;
  const next = (typeof params.next === 'string' && params.next) || '/';

  if (currentUser.id) {
    return <Redirect to={next} />;
  }

  return (
    <PageContainer showSidebar={false}>
      <div className={classes.loginPage}>
        <div className={classes.loginMain}>
          <form onSubmit={onLoginClick} className={classes.loginForm}>
            <Typography variant="h5" align="center" className={classes.title}>
              Welcome to Modern Health!
            </Typography>

            <TextField
              id="outlined-email-input"
              label="Email"
              name="email"
              autoComplete="email"
              margin="dense"
              variant="outlined"
              error={!!fieldErrors.email}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={classes.input}
              helperText={fieldErrors.email}
            />

            {loginError && (
              <Typography variant="body1" color="error" align="center">
                {loginError}
              </Typography>
            )}

            <div className={classes.loginButtonHolder}>
              {loading ? (
                <CircularProgress />
              ) : (
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={!email}
                  className={classes.loginButton}
                >
                  <Typography variant="body1" color="inherit" className={classes.loginButtonText}>
                    Login
                  </Typography>
                </Button>
              )}
            </div>
          </form>
        </div>

        <div className={classes.loginFooter}>
          <div className={classNames(classes.footerSection, classes.leftFooterSection)}>
            <Typography variant="h4" color="inherit" className={classes.modernHealthFooterText}>
              MODERN HEALTH
            </Typography>
            <div>
              <Link
                href="https://www.joinmodernhealth.com/privacy-policy/index.html"
                className={classes.hrefUnderLine}
              >
                <Typography variant="body2" color="inherit" className={classes.normalFooterText}>
                  Privacy Policy
                </Typography>
              </Link>
              <Link
                href="https://www.joinmodernhealth.com/terms-of-service/index.html"
                className={classes.hrefUnderLine}
              >
                <Typography variant="body2" color="inherit" className={classes.normalFooterText}>
                  Terms of Service
                </Typography>
              </Link>
            </div>
            <Typography variant="body2" color="inherit" className={classes.normalFooterText}>
              ©{new Date().getFullYear()} Modern Health Inc. All rights reserved.
            </Typography>
          </div>
          <div className={classNames(classes.footerSection, classes.rightFooterSection)}>
            <Typography variant="body2" color="inherit" className={classes.normalFooterText}>
              450 Sansome St.
            </Typography>
            <Typography variant="body2" color="inherit" className={classes.normalFooterText}>
              San Francisco, CA 94111
            </Typography>
            <Typography variant="body2" color="inherit" className={classes.normalFooterText}>
              provider@joinmodernhealth.com
            </Typography>
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

export default SsoLogin;
